import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';
import {
  Email as EmailIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
// jspdf + jspdf-autotable are dynamically imported inside handleGeneratePDF so
// the ~200 KB jspdf chunk only loads when the user clicks "Generate PDF".
import logo from '../../assets/images/logo.png';
import { Client } from '../../types/client';
import { Facture } from '../../types/facture';
import { FactureAvoir } from '../../types/factureAvoir';
import { Encaissement } from '../../types/encaissement';
import { clientsAPI, facturesAPI, facturesAvoirAPI, encaissementsAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import { extractErrorMessage } from '../../utils/errorHandler';
import usePdfPreview from '../../hooks/usePdfPreview';
import FacturePreview from './FacturePreview';
import FactureAvoirPreview from './FactureAvoirPreview';
import PermissionWrapper from '../../components/PermissionWrapper';
import EmailComposeDialog from '../../components/EmailComposeDialog';
import ColumnFilter from '../../components/ColumnFilter';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { FilterListOff as FilterListOffIcon } from '@mui/icons-material';

const COMMERCIAL_COLOR = '#4CAF50';

// Client-side column filters for the assembled compte-client rows
const COMPTE_CLIENT_FILTER_SCHEMA = {
  date:      { type: 'dateRange',   paramFrom: 'date_from', paramTo: 'date_to' },
  reference: { type: 'text',        param: 'reference' },
  libelle:   { type: 'text',        param: 'libelle' },
  debit:     { type: 'numberRange', paramMin: 'debit_min', paramMax: 'debit_max' },
  credit:    { type: 'numberRange', paramMin: 'credit_min', paramMax: 'credit_max' },
  solde:     { type: 'numberRange', paramMin: 'solde_min', paramMax: 'solde_max' },
} as const;

// Union type for combined rows (factures, factures avoir, and encaissements)
interface UnifiedRow {
  type: 'facture' | 'avoir' | 'encaissement';
  id: number;
  reference: string;
  date: string;
  total_net: number;
  debit: number;
  credit: number;
  solde: number;
  status: string;
  libelle: string;
  code: string;         // Short code: FAC, AVR, RGL
  nBon: string;         // N° bon: facture ref or encaissement ref
  nBC: string;          // N°B.C.: bc_numero for factures, facture_reference for encaissements
  items: { designation: string; quantite: number }[];
  originalData: Facture | FactureAvoir | Encaissement;
}

interface ClientSummary {
  total_payee: number;
  total_en_cours: number;
  total_impayee: number;
  total_avoir: number;
  plafond_fixe: number;
  plafond_depasse: number;
  chiffre_affaires: number;
}

const CompteClientManagement: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');
  const [situations, setSituations] = useState<string[]>([]);
  const [selectedSituation, setSelectedSituation] = useState<string>('');
  const [summary, setSummary] = useState<ClientSummary | null>(null);
  const [unifiedRows, setUnifiedRows] = useState<UnifiedRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [previewFacture, setPreviewFacture] = useState<Facture | null>(null);
  const [previewFactureAvoir, setPreviewFactureAvoir] = useState<FactureAvoir | null>(null);
  const { filters, update, reset, hasActive } = useColumnFilters(
    COMPTE_CLIENT_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const { alertState, showError, showSuccess, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const data = await clientsAPI.getAllForDropdown();
        setClients(data);
      } catch (err) {
        showError('Erreur lors du chargement des clients');
      }
    };
    fetchClients();
  }, []);

  // Fetch situations when client is selected
  useEffect(() => {
    if (selectedClientId) {
      const fetchSituations = async () => {
        try {
          const data = await facturesAPI.getClientSituations(selectedClientId as number);
          setSituations(data);
          setSelectedSituation(''); // Reset situation filter
        } catch (err) {
          showError('Erreur lors du chargement des situations');
        }
      };
      fetchSituations();
    } else {
      setSituations([]);
      setSelectedSituation('');
    }
  }, [selectedClientId]);

  // Fetch summary when client is selected
  useEffect(() => {
    if (selectedClientId) {
      const fetchSummary = async () => {
        try {
          const data = await facturesAPI.getClientSummary(selectedClientId as number);
          setSummary(data);
        } catch (err) {
          showError('Erreur lors du chargement du résumé');
        }
      };
      fetchSummary();
    } else {
      setSummary(null);
    }
  }, [selectedClientId]);

  // Store all rows (unpaginated) for PDF export
  const [allRows, setAllRows] = useState<UnifiedRow[]>([]);

  // Fetch factures, factures avoir, and encaissements when client or situation changes
  const fetchData = useCallback(async () => {
    if (!selectedClientId) {
      setUnifiedRows([]);
      setAllRows([]);
      setTotalCount(0);
      return;
    }

    try {
      setLoading(true);

      // Fetch factures, factures avoir, and encaissements in parallel
      const [facturesResponse, avoirResponse, encaissementsResponse] = await Promise.all([
        facturesAPI.getByClientId(
          selectedClientId as number,
          0,
          1000,
          selectedSituation || undefined
        ),
        (!selectedSituation || selectedSituation === 'avoir')
          ? facturesAvoirAPI.getByClientId(selectedClientId as number, 0, 1000)
          : Promise.resolve({ items: [], total: 0 }),
        (!selectedSituation || selectedSituation === 'encaissement')
          ? encaissementsAPI.getPaginated(0, 1000, undefined, selectedClientId as number)
          : Promise.resolve({ items: [], total: 0, total_montant: 0 })
      ]);

      // Convert factures to unified rows (debit entries)
      const factureRows: UnifiedRow[] = facturesResponse.items.map(f => ({
        type: 'facture' as const,
        id: f.id,
        reference: f.reference,
        date: f.date,
        total_net: Number(f.total_net),
        debit: Number(f.total_net),
        credit: 0,
        solde: 0,
        status: f.status,
        libelle: f.items?.map(item => item.designation).join(', ') || '-',
        code: 'FAC',
        nBon: f.reference,
        nBC: f.bc_numero || '',
        items: f.items?.map(item => ({ designation: item.designation, quantite: item.quantite })) || [],
        originalData: f
      }));

      // Convert factures avoir to unified rows (credit entries)
      const avoirRows: UnifiedRow[] = (!selectedSituation || selectedSituation === 'avoir')
        ? avoirResponse.items.map(a => ({
            type: 'avoir' as const,
            id: a.id,
            reference: a.reference,
            date: a.date,
            total_net: Number(a.total_net),
            debit: 0,
            credit: Number(a.total_net),
            solde: 0,
            status: a.status,
            libelle: a.items?.map(item => item.designation).join(', ') || '-',
            code: 'AVR',
            nBon: a.reference,
            nBC: '',
            items: a.items?.map(item => ({ designation: item.designation, quantite: item.quantite })) || [],
            originalData: a
          }))
        : [];

      // Convert encaissements to unified rows (credit entries)
      const encaissementRows: UnifiedRow[] = (!selectedSituation || selectedSituation === 'encaissement')
        ? encaissementsResponse.items.map(e => ({
            type: 'encaissement' as const,
            id: e.id,
            reference: e.mode_paiement_nom || e.reference,
            date: e.date,
            total_net: Number(e.montant_encaisse),
            debit: 0,
            credit: Number(e.montant_encaisse),
            solde: 0,
            status: e.status,
            libelle: e.libelle || (e.facture_reference ? `Paiement Facture N° ${e.facture_reference}` : 'Paiement'),
            code: 'PAC',
            nBon: e.reference,
            nBC: e.facture_reference || '',
            items: [],
            originalData: e
          }))
        : [];

      // Combine all rows
      let combinedRows = [...factureRows, ...avoirRows, ...encaissementRows];

      // If filtering by specific situation
      if (selectedSituation === 'avoir') {
        combinedRows = avoirRows;
      } else if (selectedSituation === 'encaissement') {
        combinedRows = encaissementRows;
      }

      // Sort by date ascending (oldest first) for running solde calculation
      combinedRows.sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      // Calculate running solde (debit increases, credit decreases)
      let runningSolde = 0;
      for (const row of combinedRows) {
        runningSolde += row.debit - row.credit;
        row.solde = runningSolde;
      }

      // Reverse to show most recent first
      combinedRows.reverse();

      setAllRows(combinedRows);
      // Filtering + pagination is handled by the effect below
    } catch (err) {
      showError('Erreur lors du chargement des données');
      setUnifiedRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selectedSituation]);

  // Apply column filters + pagination whenever rows, filters, or page state change
  useEffect(() => {
    const dateFrom = filters.date.from ? new Date(filters.date.from).getTime() : null;
    const dateTo = filters.date.to ? new Date(filters.date.to).getTime() : null;
    const refQ = filters.reference.trim().toLowerCase();
    const libQ = filters.libelle.trim().toLowerCase();
    const debitMin = filters.debit.min !== '' ? Number(filters.debit.min) : null;
    const debitMax = filters.debit.max !== '' ? Number(filters.debit.max) : null;
    const creditMin = filters.credit.min !== '' ? Number(filters.credit.min) : null;
    const creditMax = filters.credit.max !== '' ? Number(filters.credit.max) : null;
    const soldeMin = filters.solde.min !== '' ? Number(filters.solde.min) : null;
    const soldeMax = filters.solde.max !== '' ? Number(filters.solde.max) : null;

    const filtered = allRows.filter((row) => {
      const ts = new Date(row.date).getTime();
      if (dateFrom !== null && ts < dateFrom) return false;
      if (dateTo !== null && ts > dateTo) return false;
      if (refQ && !row.reference.toLowerCase().includes(refQ)) return false;
      if (libQ && !row.libelle.toLowerCase().includes(libQ)) return false;
      if (debitMin !== null && row.debit < debitMin) return false;
      if (debitMax !== null && row.debit > debitMax) return false;
      if (creditMin !== null && row.credit < creditMin) return false;
      if (creditMax !== null && row.credit > creditMax) return false;
      if (soldeMin !== null && row.solde < soldeMin) return false;
      if (soldeMax !== null && row.solde > soldeMax) return false;
      return true;
    });

    setTotalCount(filtered.length);
    const skip = page * rowsPerPage;
    setUnifiedRows(filtered.slice(skip, skip + rowsPerPage));
  }, [allRows, filters, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [selectedClientId, selectedSituation]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusLabel = (status: string, type: 'facture' | 'avoir' | 'encaissement' = 'facture') => {
    if (type === 'avoir') {
      const labels: Record<string, string> = {
        brouillon: 'Brouillon',
        validee: 'Validée',
        annulee: 'Annulée',
      };
      return labels[status] || status;
    }
    const labels: Record<string, string> = {
      en_cours: 'En Cours',
      encaisse: 'Encaissé',
      impaye: 'Impayé',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' => {
    const colors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
      en_cours: 'warning',
      encaisse: 'success',
      impaye: 'error',
    };
    return colors[status] || 'default';
  };

  // Get designations as comma-separated string (for display)
  const getDesignationsFromRow = (row: UnifiedRow): string => {
    if (!row.items || row.items.length === 0) {
      return '-';
    }
    return row.items.map(item => item.designation).join(', ');
  };

  // Get designations for tooltip (one per line)
  const getDesignationsForTooltipFromRow = (row: UnifiedRow): React.ReactNode => {
    if (!row.items || row.items.length === 0) {
      return '-';
    }
    return (
      <Box>
        {row.items.map((item, index) => (
          <Box key={index}>• {item.designation} (Qté: {item.quantite})</Box>
        ))}
      </Box>
    );
  };

  // Compute total solde from all rows
  const totalSolde = allRows.length > 0 ? allRows[0].solde : 0; // First row (most recent) has the final solde

  // Format number for PDF: 95.576,40 (dots as thousands, comma as decimal)
  const formatPdfAmount = (amount: number): string => {
    return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, '.').replace(/\.(\d{2})$/, ',$1');
  };

  const handleGeneratePDF = async () => {
    if (!selectedClient || allRows.length === 0) return;

    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const today = new Date().toLocaleDateString('fr-FR');
    const marginLeft = 14;
    const marginRight = 14;

    // --- Header ---
    let y = 12;

    // Logo left (smaller, matching target)
    try {
      doc.addImage(logo, 'PNG', marginLeft, y, 45, 20);
    } catch (e) {
      // Logo not available
    }

    // Right side: "Imprimé le" small, then title, then client name — all compact
    const rightX = pageWidth / 2 + 15;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Imprimé le : ${today}`, pageWidth - marginRight, y + 2, { align: 'right' });

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('RELEVÉ DE COMPTE CLIENT', rightX + (pageWidth - marginRight - rightX) / 2, y + 12, { align: 'center' });

    doc.setFontSize(10);
    doc.text(`Compte : ${selectedClient.name}`, rightX + (pageWidth - marginRight - rightX) / 2, y + 19, { align: 'center' });

    // --- Table --- (close to header like the target)
    y = 40;

    const sortedForPdf = [...allRows].reverse(); // oldest first

    const tableData = sortedForPdf.map(row => [
      new Date(row.date).toLocaleDateString('fr-FR'),
      row.code,
      row.nBon,
      row.libelle.length > 50 ? row.libelle.substring(0, 50) + '...' : row.libelle,
      row.debit > 0 ? formatPdfAmount(row.debit) : '',
      row.credit > 0 ? formatPdfAmount(row.credit) : '',
      row.nBC,
      formatPdfAmount(row.solde),
    ]);

    // TOTAL row
    const totalDebit = allRows.reduce((sum, r) => sum + r.debit, 0);
    const totalCredit = allRows.reduce((sum, r) => sum + r.credit, 0);
    tableData.push([
      '', '', '', { content: 'TOTAL :', styles: { halign: 'right' as const, fontStyle: 'bold' as const } } as any,
      formatPdfAmount(totalDebit),
      formatPdfAmount(totalCredit),
      '',
      '',
    ]);

    autoTable(doc, {
      startY: y,
      margin: { left: marginLeft, right: marginRight },
      head: [['Date', 'Code', 'N° bon', 'Libellé', 'Débit', 'Crédit', 'N°B.C.', 'Solde']],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 1.5, lineColor: [0, 0, 0], lineWidth: 0.2, overflow: 'linebreak' },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        cellPadding: 2,
      },
      bodyStyles: { textColor: [0, 0, 0] },
      columnStyles: {
        0: { cellWidth: 20 },   // Date
        1: { cellWidth: 12 },   // Code
        2: { cellWidth: 22 },   // N° bon
        3: { cellWidth: 'auto' }, // Libellé (fills remaining)
        4: { cellWidth: 20, halign: 'right' },  // Débit
        5: { cellWidth: 20, halign: 'right' },  // Crédit
        6: { cellWidth: 22 },   // N°B.C.
        7: { cellWidth: 20, halign: 'right', fontStyle: 'bold' },  // Solde
      },
      theme: 'grid',
      didParseCell: (data: any) => {
        // Bold the TOTAL row
        if (data.section === 'body' && data.row.index === sortedForPdf.length) {
          data.cell.styles.fontStyle = 'bold';
          if (data.column.index <= 3) {
            data.cell.styles.lineWidth = 0;
          }
        }
      },
    });

    // --- Solde Créditeur / Débiteur box ---
    const finalY = (doc as any).lastAutoTable?.finalY || y + 20;
    const soldeLabel = totalSolde <= 0 ? 'Solde Créditeur' : 'Solde Débiteur';
    const soldeValue = formatPdfAmount(Math.abs(totalSolde));

    // Draw box aligned to the right
    const boxX = pageWidth - 14 - 70;
    const boxY = finalY + 8;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(boxX, boxY, 40, 10);
    doc.rect(boxX + 40, boxY, 30, 10);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(soldeLabel, boxX + 20, boxY + 6.5, { align: 'center' });
    doc.text(soldeValue, boxX + 55, boxY + 6.5, { align: 'center' });

    // --- Footer ---
    const footerY = boxY + 25;
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.5);
    doc.line(14, footerY, pageWidth - 14, footerY);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(128, 128, 128);
    doc.text(`Document généré le ${today} — MAF AGENCEMENT`, pageWidth / 2, footerY + 8, { align: 'center' });

    // Open the PDF preview; the user prints/downloads from the viewer toolbar.
    openPreview({
      getBlob: async () => doc.output('blob'),
      fileName: `Releve_Compte_${selectedClient.code}_${new Date().toISOString().split('T')[0]}.pdf`,
    });
  };

  const handleEmail = () => {
    const selectedClient = clients.find(c => c.id === selectedClientId);
    if (!selectedClient?.email) {
      showError('Ce client n\'a pas d\'adresse email configurée');
      return;
    }
    setEmailDialogOpen(true);
  };

  // Get selected client info
  const selectedClient = clients.find(c => c.id === selectedClientId);

  return (
    <PermissionWrapper
      moduleName="Commercial"
      miniModuleName="Compte Client"
      operation="view"
      fallback={
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Vous n'avez pas la permission d'accéder à cette page.
          </Typography>
        </Box>
      }
    >
      <Box sx={{ py: 3, px: 0 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#4CAF50' }}>
          Compte Client / Relance et Recouvrement
        </Typography>

      {/* Filters Row */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Autocomplete
              size="small"
              options={clients}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              value={clients.find(c => c.id === selectedClientId) || null}
              onChange={(_, newValue) => {
                setSelectedClientId(newValue ? newValue.id : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  placeholder="Rechercher un client..."
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.code} - {option.name}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="Aucun client trouvé"
              clearText="Effacer"
              openText="Ouvrir"
              closeText="Fermer"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small" disabled={!selectedClientId}>
              <InputLabel>Situation</InputLabel>
              <Select
                value={selectedSituation}
                label="Situation"
                onChange={(e) => setSelectedSituation(e.target.value)}
              >
                <MenuItem value="">
                  <em>Toutes les situations</em>
                </MenuItem>
                {situations.map((situation) => (
                  <MenuItem key={situation} value={situation}>
                    {getStatusLabel(situation)}
                  </MenuItem>
                ))}
                <MenuItem value="avoir" sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}>
                  <em style={{ color: '#E65100' }}>Factures Avoir</em>
                </MenuItem>
                <MenuItem value="encaissement">
                  <em style={{ color: '#2E7D32' }}>Encaissements</em>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<PdfIcon />}
              onClick={handleGeneratePDF}
              disabled={!selectedClientId || allRows.length === 0}
              sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' }, textTransform: 'none' }}
            >
              Générer l'état
            </Button>
            <Tooltip title="Envoyer par Email">
              <IconButton
                onClick={handleEmail}
                disabled={!selectedClientId}
                sx={{ bgcolor: '#4CAF50', color: 'white', '&:hover': { bgcolor: '#388E3C' } }}
              >
                <EmailIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Message when no client selected */}
      {!selectedClientId && (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
          <Typography variant="h6" color="text.secondary">
            Veuillez sélectionner un client
          </Typography>
        </Paper>
      )}

      {/* Summary Cards - Only show when client is selected */}
      {selectedClientId && summary && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {[
            { label: "CHIFFRE D'AFFAIRES", value: summary.chiffre_affaires, bg: '#E8EAF6', border: '#3F51B5', color: '#303F9F' },
            { label: 'PAYÉE', value: summary.total_payee, bg: '#E8F5E9', border: '#4CAF50', color: '#2E7D32' },
            { label: 'EN COURS', value: summary.total_en_cours, bg: '#FFF3E0', border: '#FF9800', color: '#E65100' },
            { label: 'IMPAYÉE', value: summary.total_impayee, bg: '#FFEBEE', border: '#F44336', color: '#C62828' },
            { label: 'AVOIR', value: summary.total_avoir, bg: '#FFF3E0', border: '#FF9800', color: '#E65100' },
            { label: 'PLAFOND FIXÉ', value: summary.plafond_fixe, bg: '#E3F2FD', border: '#2196F3', color: '#1565C0' },
            { label: 'PLAFOND DÉPASSÉ', value: summary.plafond_depasse, bg: summary.plafond_depasse > 0 ? '#FFEBEE' : '#E8F5E9', border: summary.plafond_depasse > 0 ? '#F44336' : '#4CAF50', color: summary.plafond_depasse > 0 ? '#C62828' : '#2E7D32' },
            { label: 'TOTAL SOLDE', value: totalSolde, bg: totalSolde > 0 ? '#FFF3E0' : '#E8F5E9', border: totalSolde > 0 ? '#FF9800' : '#4CAF50', color: totalSolde > 0 ? '#E65100' : '#2E7D32' },
          ].map((card) => (
            <Grid item xs={6} sm={4} md={1.5} key={card.label}>
              <Card sx={{ bgcolor: card.bg, borderLeft: `3px solid ${card.border}` }}>
                <CardContent sx={{ px: 1.5, py: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600, lineHeight: 1.2 }}>
                    {card.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: card.color, fontSize: '0.85rem', mt: 0.25 }}>
                    {formatCurrency(card.value)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Factures & Factures Avoir Table - Only show when client is selected */}
      {selectedClientId && (
        <>
        {hasActive() && (
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              Effacer les filtres
            </Button>
          </Box>
        )}
        <TableContainer component={Paper} sx={getModuleTableTheme('commercial')}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date
                    <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={COMMERCIAL_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Référence
                    <ColumnFilter type="text" value={filters.reference} onChange={(v) => update('reference', v)} label="référence" color={COMMERCIAL_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Libellé
                    <ColumnFilter type="text" value={filters.libelle} onChange={(v) => update('libelle', v)} label="libellé" color={COMMERCIAL_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    Débit
                    <ColumnFilter type="numberRange" value={filters.debit} onChange={(v) => update('debit', v)} label="débit" color={COMMERCIAL_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    Crédit
                    <ColumnFilter type="numberRange" value={filters.credit} onChange={(v) => update('credit', v)} label="crédit" color={COMMERCIAL_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    Solde
                    <ColumnFilter type="numberRange" value={filters.solde} onChange={(v) => update('solde', v)} label="solde" color={COMMERCIAL_COLOR} />
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Situation</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : unifiedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Aucune donnée trouvée
                  </TableCell>
                </TableRow>
              ) : (
                unifiedRows.map((row) => (
                  <TableRow
                    key={`${row.type}-${row.id}`}
                    hover
                    sx={{
                      bgcolor: row.type === 'encaissement' ? '#F1F8E9' : row.type === 'avoir' ? '#FFF8E1' : 'inherit',
                    }}
                  >
                    <TableCell>{formatDate(row.date)}</TableCell>
                    <TableCell>{row.reference}</TableCell>
                    <TableCell>
                      <Tooltip title={row.type === 'encaissement' ? row.libelle : getDesignationsForTooltipFromRow(row)} arrow>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 250,
                            cursor: 'pointer',
                          }}
                        >
                          {row.type === 'encaissement' ? row.libelle : getDesignationsFromRow(row)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: row.debit > 0 ? 600 : 400 }}>
                      {row.debit > 0 ? formatCurrency(row.debit) : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#2E7D32', fontWeight: row.credit > 0 ? 600 : 400 }}>
                      {row.credit > 0 ? formatCurrency(row.credit) : ''}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 600,
                      color: row.solde > 0 ? '#C62828' : '#2E7D32',
                    }}>
                      {formatCurrency(row.solde)}
                    </TableCell>
                    <TableCell>
                      {row.type === 'avoir' ? (
                        <Chip
                          label="Avoir"
                          size="small"
                          sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 500 }}
                        />
                      ) : row.type === 'encaissement' ? (
                        <Chip
                          label="Encaissement"
                          size="small"
                          sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 500 }}
                        />
                      ) : (
                        <Chip
                          label={getStatusLabel(row.status, row.type)}
                          color={getStatusColor(row.status)}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {row.type !== 'encaissement' && (
                        <Tooltip title="Voir">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (row.type === 'facture') {
                                setPreviewFacture(row.originalData as Facture);
                              } else if (row.type === 'avoir') {
                                setPreviewFactureAvoir(row.originalData as FactureAvoir);
                              }
                            }}
                            sx={{ color: '#4CAF50' }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <CustomTablePagination
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
        </>
      )}

      {/* Preview Dialog - Facture */}
      {previewFacture && (
        <FacturePreview
          facture={previewFacture}
          open={!!previewFacture}
          onClose={() => setPreviewFacture(null)}
          onEdit={() => {}}
        />
      )}

      {/* Preview Dialog - Facture Avoir */}
      {previewFactureAvoir && (
        <FactureAvoirPreview
          avoir={previewFactureAvoir}
          open={!!previewFactureAvoir}
          onClose={() => setPreviewFactureAvoir(null)}
          onEdit={() => {}}
        />
      )}

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* Email Compose Dialog */}
      {selectedClient && (
        <EmailComposeDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          toEmail={selectedClient.email || ''}
          toName={selectedClient.contact_name || selectedClient.name}
          eventCode="COMPTE_CLIENT_RELANCE"
          onSuccess={() => showSuccess('Email envoyé avec succès')}
          title={`Envoyer un email à ${selectedClient.name}`}
        />
      )}

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
      </Box>
    </PermissionWrapper>
  );
};

export default CompteClientManagement;
